import { AnimatePresence } from 'framer-motion';
import { FC, memo, useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import useStorageToggle from '@/hooks/useStorageToggle';
import { OptimisationTestUnitResource } from '@/types/resources';
import {
  OptimisationProjectNetworkEnum,
  OptimisationTestTypeEnum,
} from '@/utils/enums';

import DeleteAllUnitsButton from './DeleteAllUnitsButton';
import GoogleAdsExportButton from './GoogleAdsExportButton';
import SequencesAndIntrosFormModal from './SequencesAndIntrosFormModal';
import TestHeading from './TestHeading';
import TestTable from './TestTable';
import TestsStatusHolder from './TestsStatusHolder';

const ButtonWrapper = styled.div`
  justify-content: flex-end;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  > button {
    padding-left: 16px;
    padding-right: 16px;
    font-weight: 600;
  }
`;

interface Props {
  tests?: OptimisationTestUnitResource[];
  network: OptimisationProjectNetworkEnum;
  externalProjectId: string;
  type: OptimisationTestTypeEnum.SEQUENCES | OptimisationTestTypeEnum.INTRO;
  projectTitle: string;
}

const SequencesAndIntros: FC<Props> = memo(
  ({
    type = OptimisationTestTypeEnum.SEQUENCES,
    tests,
    network,
    externalProjectId,
    projectTitle,
  }) => {
    const [showTestModal, setShowTestModal] = useState(false);
    const [isOpen, toggleIsOpen] = useStorageToggle(
      `optimisation_test_${externalProjectId}_${OptimisationTestTypeEnum.SEQUENCES ? 'sequences' : 'intros'}`,
    );

    const getHeadline = () => {
      if (type === OptimisationTestTypeEnum.SEQUENCES) {
        return 'Sequences';
      }

      return 'Intros';
    };

    const getDescription = () => {
      if (type === OptimisationTestTypeEnum.SEQUENCES) {
        return 'Tests the best order of clips to keep viewers engaged';
      }

      return 'Evaluates the impact of adding an introduction to the sequence on viewer engagement';
    };

    const batches = useMemo(
      () =>
        tests!.reduce<number[]>((acc, curr) => {
          if (acc.includes(curr.batch)) {
            return acc;
          }

          return [...acc, curr.batch];
        }, []),
      [tests],
    );

    return (
      <>
        <TestHeading
          heading={getHeadline()}
          description={getDescription()}
          onToggle={toggleIsOpen}
          isOpen={isOpen}
        />
        {isOpen && (
          <>
            <TestTable
              type={type}
              tests={tests}
              network={network}
              testParameter="estimatedFinalValue"
            />
            <ButtonWrapper>
              <TestsStatusHolder tests={tests!} />
              <GoogleAdsExportButton
                type={type}
                externalProjectId={externalProjectId}
              />
              <DeleteAllUnitsButton type={type} batches={batches} />
              <Button onClick={() => setShowTestModal(true)} $type="colorized">
                Upload tests
              </Button>
            </ButtonWrapper>
            <AnimatePresence>
              {showTestModal && (
                <SequencesAndIntrosFormModal
                  network={network}
                  type={type}
                  onClose={() => setShowTestModal(false)}
                  projectTitle={projectTitle}
                />
              )}
            </AnimatePresence>
          </>
        )}
      </>
    );
  },
);

export default SequencesAndIntros;
